import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { FiCheckCircle, FiDownload, FiFileText, FiX } from "react-icons/fi";
import { CAR_REPORT, CAR_REPORT_DETAIL } from "../../constant";
import { POST } from "../../constant/apiMethods";
import Labels from "../../services/apiLabels";
import { conditionReportPdfHref } from "./printInspectionReport";
import "../Car Report/report.css";
import InspectionReportBody, {
  unwrapReportList,
  overallFromSections,
} from "../Car Report/InspectionReportBody";

function cleanValue(value) {
  if (value == null || value === "" || value === "None" || value === "N/A") {
    return null;
  }
  return String(value).trim() || null;
}

function isCompletedReport(payload, sections, damagePoints) {
  if (unwrapReportList(sections).length > 0 || unwrapReportList(damagePoints).length > 0) {
    return true;
  }
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return false;
  }
  return Number(payload.total_inspect) > 0;
}

function getVehicleName(sections, fallbackItem) {
  for (const section of sections || []) {
    for (const item of section?.inspection_title || []) {
      const selected = item?.selected_inspection_question?.[0];
      const name = cleanValue(selected?.car);
      if (name) return name;
    }
  }
  return (
    cleanValue(fallbackItem?.product_name) ||
    [cleanValue(fallbackItem?.brands), cleanValue(fallbackItem?.model_name)]
      .filter(Boolean)
      .join(" ") ||
    "Vehicle inspection report"
  );
}

function getProductId(item) {
  return (
    item?.product_id ??
    item?.productId ??
    item?.product_code ??
    item?.id ??
    null
  );
}

function VehicleReportModal({
  open,
  item,
  onClose,
  car_report_data,
  car_report_detail_data,
  loading,
  detailLoading,
  car_report_call,
  car_report_detail_call,
}) {
  const productId = getProductId(item);

  useEffect(() => {
    if (!open || productId == null || productId === "") return undefined;
    car_report_call({ id: productId });
    car_report_detail_call({ id: productId });

    const onKey = (event) => {
      if (event.key !== "Escape") return;
      if (document.querySelector(".dd-photo-viewer")) return;
      onClose?.();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, productId, onClose]);

  const reportPayload = useMemo(() => {
    if (!car_report_detail_data || typeof car_report_detail_data !== "object") {
      return null;
    }
    return car_report_detail_data;
  }, [car_report_detail_data]);

  const sections = useMemo(
    () => unwrapReportList(car_report_detail_data),
    [car_report_detail_data]
  );
  const damagePoints = useMemo(
    () => unwrapReportList(car_report_data),
    [car_report_data]
  );
  const completed = isCompletedReport(reportPayload, sections, damagePoints);
  const calculated = overallFromSections(sections, damagePoints);
  const score = calculated.parts.length
    ? calculated.score
    : Math.round(Number(reportPayload?.total_inspect) || 0);
  const vehicleName = getVehicleName(sections, item);
  const busy = Boolean(loading || detailLoading);

  const pdfHref = completed ? conditionReportPdfHref(productId) : "";

  if (!open || !item) return null;

  return (
    <div
      className="pi-report-modal"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="pi-report-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Inspection report"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="pi-report-modal__toolbar no-print">
          <div>
            <p className="pi-report-modal__toolbar-label">Inspection report</p>
            <strong>{vehicleName}</strong>
          </div>
          <div className="pi-report-modal__toolbar-actions">
            {pdfHref ? (
              <a
                className="pi-report-modal__download"
                href={pdfHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FiDownload size={16} aria-hidden />
                Download PDF
              </a>
            ) : null}
            <button
              type="button"
              className="pi-report-modal__close"
              onClick={onClose}
              aria-label="Close report"
            >
              <FiX size={18} aria-hidden />
            </button>
          </div>
        </div>

        <div className="pi-report-modal__body">
          <div className="pi-report pi-report--modal">
            <div className="pi-report__shell pi-report__shell--modal">
              {busy && !completed ? (
                <div className="pi-report__loading" aria-live="polite">
                  <span className="pi-report__spinner" aria-hidden />
                  Loading inspection report…
                </div>
              ) : null}

              {!busy && !completed ? (
                <div className="pi-report__empty">
                  <FiFileText size={28} aria-hidden />
                  <h1>Final report not ready</h1>
                  <p>
                    Reports are generated after vehicle registration and a
                    completed inspection. This car does not have a final
                    inspection report yet.
                  </p>
                  <ol className="pi-report__steps">
                    <li>Vehicle registration</li>
                    <li>Inspection in progress</li>
                    <li>Final report published</li>
                  </ol>
                </div>
              ) : null}

              {completed ? (
                <>
                  <header className="pi-report__hero">
                    <div className="pi-report__hero-copy">
                      <p className="pi-report__eyebrow">Inspection report</p>
                      <h1>{vehicleName}</h1>
                      <p className="pi-report__lead">
                        Final condition report generated after registration and
                        completed inspection.
                      </p>
                      <div
                        className="pi-report__flow"
                        aria-label="Report process"
                      >
                        <span>
                          <FiCheckCircle size={14} aria-hidden /> Registered
                        </span>
                        <span>
                          <FiCheckCircle size={14} aria-hidden /> Inspected
                        </span>
                        <span className="is-active">
                          <FiCheckCircle size={14} aria-hidden /> Final report
                        </span>
                      </div>
                    </div>
                    <div
                      className="pi-report__score"
                      aria-label={`Overall inspection score ${score} out of 100`}
                    >
                      <strong>{score}</strong>
                      <span>/100</span>
                      <em>Overall score</em>
                    </div>
                  </header>

                  <InspectionReportBody
                    resetKey={productId}
                    sections={sections}
                    damagePoints={damagePoints}
                  />

                  <div className="pi-report-modal__print-note no-print">
                    Download opens the full condition report PDF.
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  car_report_data: state.carreport.car_report_data,
  car_report_detail_data: state.carreport.car_report_detail_data,
  loading: Boolean(state.carreport.loading),
  detailLoading: Boolean(state.carreport.winerListloading),
});

const mapDispatchToProps = (dispatch) => ({
  car_report_call: (data) =>
    dispatch({
      type: CAR_REPORT,
      payload: {
        type: CAR_REPORT,
        method: POST,
        label: Labels.car_report,
        hideOverlayLoader: true,
        data,
      },
    }),
  car_report_detail_call: (data) =>
    dispatch({
      type: CAR_REPORT_DETAIL,
      payload: {
        type: CAR_REPORT_DETAIL,
        method: POST,
        label: Labels.car_report_detail,
        hideOverlayLoader: true,
        data,
      },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(VehicleReportModal);
